import {
    BreakingNewsCard,
    CollectionEvent,
    createCardClickedEvent,
    createCollectionAvailableEvent,
    PublicationCardItem,
    useFeature,
} from '@news-mono/web-common'
import React from 'react'
import {
    BreakingNewsThemeName,
    BreakingNewsThemeNames,
    StyledAnnouncementMark,
    StyledBreakingNews,
    StyledBreakingNewsMark,
    StyledBreakingNewsTitle,
    StyledCricketPlayerImage,
    StyledCricketPlayerWrapper,
    StyledFederalElection2022Logo,
    StyledGrandfinalLogo,
    StyledGrandfinalWrapper,
    StyledIconChevronRight,
    StyledLogoContainer,
    StyledPick11Logo,
    StyledSevenCricketLogo,
    StyledStateElectionLogo,
    StyledStateElectionWrapper,
    StyledTheGameLogo,
    StyledWrapper,
} from '../../content/BreakingNews/BreakingNews.styled'
import { ImpressionAvailable } from '../../__helpers/impression-available-helper'

export interface BreakingNewsProps {
    titlePrefix?: string
    breakingNewsTheme: string | undefined
    item: PublicationCardItem
    removeBottomMargin?: boolean
    onEvent: (event: CollectionEvent) => void
}

function getTheme(theme: string | undefined): BreakingNewsThemeName {
    if (BreakingNewsThemeNames.indexOf(theme as BreakingNewsThemeName) > -1) {
        return theme as BreakingNewsThemeName
    }
    return 'default'
}

export const renderTitlePrefix = (
    titlePrefix: string | undefined,
    newsTheme: BreakingNewsThemeName | undefined,
) => {
    if (!titlePrefix && newsTheme) {
        switch (newsTheme) {
            case 'announcement':
            case 'thegame':
            case 'state-election':
            case 'blackfriday':
            case 'federal-election-2022':
                return ''
            case 'cricket':
                return 'Cricket: Live and Free on Seven'
            case 'csk':
                return 'Latest:'
            default:
                return 'Breaking News:'
        }
    }

    return titlePrefix || 'Breaking News:'
}

export const BreakingNews: React.FC<BreakingNewsProps> = ({
    item,
    breakingNewsTheme,
    titlePrefix,
    onEvent,
    removeBottomMargin,
}) => {
    const isPollieRaterDriver = useFeature(
        'federal-election-2022-pollie-rater-strap-driver',
    )

    const hideOnMobileAndApp = useFeature('hide-mobile-breaking-news-banner')

    if (!item.shortHeadline) {
        return null
    }

    const isAnnouncement = breakingNewsTheme === 'announcement'

    const newItem: BreakingNewsCard = {
        ...item,
        cardType: 'breaking-news',
    }

    const cardFormat = isAnnouncement ? 'Announcement' : 'BreakingNews'

    const linkClicked = () => {
        onEvent(createCardClickedEvent(newItem, 'BreakingNews', cardFormat, 1))
    }

    const renderMark = (newsTheme: BreakingNewsThemeName | undefined) => {
        if (!newsTheme) {
            return <StyledBreakingNewsMark breakingNewsTheme={'default'} />
        }

        const theme = getTheme(newsTheme)

        switch (theme) {
            case 'default':
                return <StyledBreakingNewsMark breakingNewsTheme={'default'} />
            case 'grand-final':
            case 'blackfriday':
            case 'federal-election-2022':
                return
            default:
                return <StyledAnnouncementMark breakingNewsTheme={newsTheme} />
        }
    }

    const renderLogo = (newsTheme: BreakingNewsThemeName | undefined) => {
        if (!newsTheme) {
            return <StyledIconChevronRight />
        }

        const cricketPlayerImage = require<string>('./assets/pick11-cricketplayer.png')
        const cricketPlayerImage2x = require<string>('./assets/pick11-cricketplayer@2x.png')
        const grandfinalImage = require<string>('./assets/breaking-news-grand-final.png')

        switch (newsTheme) {
            case 'cricket':
                return (
                    <StyledLogoContainer>
                        <StyledSevenCricketLogo
                            src={require<string>('./assets/7cricketlogo.png')}
                            alt="Seven News Cricket Live and in HD"
                        />
                    </StyledLogoContainer>
                )
            case 'thegame':
                return <StyledTheGameLogo />
            case 'state-election':
                return (
                    <StyledStateElectionWrapper>
                        <StyledStateElectionLogo />
                        <StyledIconChevronRight />
                    </StyledStateElectionWrapper>
                )
            case 'csk':
                return
            case 'pick11':
                return (
                    <StyledCricketPlayerWrapper>
                        <StyledCricketPlayerImage
                            srcSet={`${cricketPlayerImage},
                                ${cricketPlayerImage2x} 2x,`}
                            src={cricketPlayerImage}
                        />
                        <StyledPick11Logo />
                    </StyledCricketPlayerWrapper>
                )
            case 'grand-final':
                return (
                    <StyledGrandfinalWrapper>
                        <StyledGrandfinalLogo src={grandfinalImage} />
                    </StyledGrandfinalWrapper>
                )
            case 'blackfriday':
                return

            case 'federal-election-2022':
                return (
                    <StyledFederalElection2022Logo>
                        Federal Election 2022
                    </StyledFederalElection2022Logo>
                )
            default:
                return <StyledIconChevronRight />
        }
    }

    return (
        <ImpressionAvailable
            loading={false}
            available={() => {
                onEvent(
                    createCollectionAvailableEvent(
                        [
                            `Breaking News: ${renderTitlePrefix(
                                titlePrefix,
                                breakingNewsTheme,
                            )} ${item.shortHeadline}`,
                        ],
                        `Breaking News`,
                        {
                            cardFormat: breakingNewsTheme ?? 'default',
                        },
                    ),
                )
            }}
        >
            {(ref) => (
                // TODO You can't pass a ref down to a custom component. We should be able
                // to fix this with emotion 10. Wrapping in a div for now
                <div ref={ref}>
                    <StyledBreakingNews
                        to={
                            isPollieRaterDriver &&
                            breakingNewsTheme === 'federal-election-2022'
                                ? `/politics/federal-election/pollie-rater`
                                : item.link
                        }
                        onClick={linkClicked}
                        breakingNewsTheme={getTheme(breakingNewsTheme)}
                        removeBottomMargin={removeBottomMargin}
                        hideOnMobileAndApp={hideOnMobileAndApp}
                    >
                        <StyledWrapper>
                            {renderMark(breakingNewsTheme)}
                            <StyledBreakingNewsTitle
                                breakingNewsTheme={getTheme(breakingNewsTheme)}
                                fontScale={1}
                            >
                                {`${renderTitlePrefix(
                                    titlePrefix,
                                    breakingNewsTheme,
                                )} ${item.shortHeadline}`}
                            </StyledBreakingNewsTitle>
                        </StyledWrapper>
                        {renderLogo(breakingNewsTheme)}
                    </StyledBreakingNews>
                </div>
            )}
        </ImpressionAvailable>
    )
}
BreakingNews.displayName = 'BreakingNews'
